import { GALAXY_API_URL } from 'consts/copyTrading';

// API Response types (реальные имена полей из API)
export interface StarsApiResponse {
  stars: Array<{
    trader: string;
    pnl_usd: number | null;
    roi: number | null;
    f_roi: number | null;
    trades: number | null;
    // AI Mind fields — optional, absent until the backend ships pm_mind_coords.
    // Coordinates are normalized to [-1, 1].
    mind_x?: number | null;
    mind_y?: number | null;
    mind_z?: number | null;
    cluster_id?: number | null;
    archetype?: string | null;
  }>;
  current_block: number;
}

// Нормализованные данные звезды (после маппинга)
export interface NormalizedStar {
  wallet: string;
  pnl: number;
  roi: number;
  f_roi: number;
  totalTrades: number;
  mind: {
    x: number;
    y: number;
    z: number;
    cluster: number | null;
    archetype: string | null;
  } | null;
}

export interface NormalizedStarsResponse {
  stars: NormalizedStar[];
  current_block: number;
}

export interface LiveTradesApiResponse {
  trades: Array<{
    trader: string;
    token: string;
    symbol: string;
    logo_url?: string;
    pnl_usd: number;
    roi_pct: number;
    cost_usd: number;
    sell_block: number;
    time_ago: string;
  }>;
  last_block: number;
  current_block: number;
}

/**
 * Загружает список трейдеров (звезд) из API и нормализует поля
 */
type StarApiItem = StarsApiResponse['stars'][number];

const normalizeStar = (star: StarApiItem): NormalizedStar => ({
  wallet: star.trader,
  pnl: star.pnl_usd ?? 0,
  roi: (star.roi ?? 0) * 100,
  f_roi: (star.f_roi ?? 0) * 100,
  totalTrades: star.trades ?? 0,
  mind:
    typeof star.mind_x === 'number' &&
    typeof star.mind_y === 'number' &&
    typeof star.mind_z === 'number'
      ? {
          x: star.mind_x,
          y: star.mind_y,
          z: star.mind_z,
          cluster: star.cluster_id ?? null,
          archetype: star.archetype ?? null,
        }
      : null,
});

export const fetchStars = async (limit: number = 50000): Promise<NormalizedStarsResponse> => {
  const response = await fetch(`${GALAXY_API_URL}/stars?limit=${limit}`);

  if (!response.ok) {
    throw new Error(`Failed to fetch stars: HTTP ${response.status}`);
  }

  const data: StarsApiResponse = await response.json();

  // Маппинг полей API → внутренние имена (как в оригинале app.js:2383-2390)
  const stars: NormalizedStar[] = (data.stars || []).map(normalizeStar);

  return { stars, current_block: data.current_block };
};

/**
 * Look up a single wallet by address for galaxy search — works for ANY of the
 * 2.5M wallets in pm_traders, not just the displayed set. Returns null on 404.
 */
export const fetchStar = async (address: string): Promise<NormalizedStar | null> => {
  const response = await fetch(`${GALAXY_API_URL}/star/${address}`);
  if (response.status === 404) return null;
  if (!response.ok) {
    throw new Error(`Failed to fetch star: HTTP ${response.status}`);
  }
  const data: StarApiItem = await response.json();
  return normalizeStar(data);
};

/**
 * Загружает live trades из API
 */
export const fetchLiveTrades = async (
  limit: number = 20,
  sinceBlock?: number
): Promise<LiveTradesApiResponse> => {
  const url = sinceBlock
    ? `${GALAXY_API_URL}/live-trades?limit=${limit}&since_block=${sinceBlock}`
    : `${GALAXY_API_URL}/live-trades?limit=${limit}`;

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch live trades: HTTP ${response.status}`);
  }

  return response.json();
};

export interface TraderInfoApiResponse {
  pnl_30d: number;
  f_roi_30d: number | null;
}

/**
 * URL of the canonical trader-info endpoint used by the main
 * app.wowmax.exchange/copy-trading/137/<addr> page. We hit the same
 * source so the PnL and ROI shown in the space-galaxy TraderDetailPanel
 * match what users see on the main site.
 *
 * Note this is on a different path prefix (/api/traders) than the rest
 * of the galaxy API (/galaxy/...) — that is why GALAXY_API_URL is not
 * reused here.
 */
const TRADER_INFO_API_URL = 'https://sniper-api.wowmax.exchange/api/traders';

/**
 * Shape of the canonical trader-info response. Only the fields we
 * actually consume are typed; the full payload includes 24h/30d/90d/6m/1y
 * windows plus charts and open positions, none of which the space-galaxy
 * UI currently uses.
 */
interface TraderInfoApiRaw {
  data?: {
    period_30d?: {
      profit_loss?: number;
      followers_roi?: number | null;
    };
  };
}

/**
 * Загружает детальную информацию о трейдере по адресу кошелька.
 *
 * Maps the canonical /api/traders response into the legacy
 * TraderInfoApiResponse shape (pnl_30d / f_roi_30d) so the call site in
 * TraderDetailPanel does not need to change. Returns null on any
 * network or parsing failure — the panel falls back to the cached star
 * data in that case.
 */
export const fetchTraderInfo = async (address: string): Promise<TraderInfoApiResponse | null> => {
  try {
    // tz=-540 matches what the main site sends; the value affects only
    // chart bucket boundaries on the server side, not the period
    // aggregates we read here, but we mirror it for parity.
    const url = `${TRADER_INFO_API_URL}/${address}?tz=-540`;
    const response = await fetch(url);
    if (!response.ok) return null;

    const payload = (await response.json()) as TraderInfoApiRaw;
    const period30d = payload.data?.period_30d;
    if (!period30d || typeof period30d.profit_loss !== 'number') {
      return null;
    }

    return {
      pnl_30d: period30d.profit_loss,
      f_roi_30d: typeof period30d.followers_roi === 'number' ? period30d.followers_roi : null,
    };
  } catch (error) {
    console.error('Failed to fetch trader info:', error);
    return null;
  }
};

// =====================================================================
// Whitelist endpoint — small curated list of approved traders shown on
// app.wowmax.exchange/whitelist. Used by the spaceship's autopilot scan
// as the source of "discovered wallets" so users only land on curated
// leaders, not random hot stars from the wider /stars dataset.
//
// Endpoint is on the same host as /stars but under a separate /api
// prefix (not /galaxy), so we hardcode the full URL here rather than
// reusing GALAXY_API_URL.
// =====================================================================

export interface WhitelistTraderApi {
  rank: number;
  trader: string;
  profile_name: string;
  roi_pct: number;
  roi_pct_30d: number;
  roi_pct_7d: number;
  pnl_usdc: number;
  pnl_usdc_30d: number;
  closed_positions: number;
  win_rate_pct: number;
  age_days: number;
  avg_bet_usdc: number;
  cost_usdc_total: number;
  taker_pnl_ratio: number;
  top_category: string;
  metrics: Record<string, number>;
}

export interface WhitelistApiResponse {
  total: number;
  updated_at: string;
  returned: number;
  sort: string;
  category_filter: string | null;
  traders: WhitelistTraderApi[];
}

export interface NormalizedWhitelistTrader {
  wallet: string;
  /** Followers' ROI as a percentage (e.g. 5.86 means +5.86%). */
  roi: number;
}

const WHITELIST_API_URL = 'https://sniper-api.wowmax.exchange/api/whitelist';

/**
 * Loads the curated whitelist (~17 traders as of writing) and normalises
 * each entry to {wallet, roi}. ROI is taken from `roi_pct_30d` because
 * that's the same window the TraderDetailPanel and EventFeed display.
 *
 * Returns an empty array on any failure so the caller can fall back to
 * its existing default behaviour without crashing the UI.
 */
export const fetchWhitelist = async (): Promise<NormalizedWhitelistTrader[]> => {
  try {
    const response = await fetch(WHITELIST_API_URL);
    if (!response.ok) {
      console.error(`fetchWhitelist failed: HTTP ${response.status}`);
      return [];
    }
    const data: WhitelistApiResponse = await response.json();
    return (data.traders || []).map((t) => ({
      wallet: t.trader,
      // The API exposes ROI as a fraction (0.0586 = +5.86%); multiply by
      // 100 to match the percentage convention used elsewhere in the
      // codebase (NormalizedStar.f_roi is already in percent form).
      roi: (t.roi_pct_30d ?? t.roi_pct ?? 0) * 100,
    }));
  } catch (error) {
    console.error('Failed to fetch whitelist:', error);
    return [];
  }
};

const BSC_RPC_URL =
  'https://rpc.ankr.com/bsc/4c813f9e6a927d07298380075afdb1b8d1729d7548d9aa636c197e76914b6238';

/**
 * Получает текущий номер блока BSC через RPC
 */
export const fetchBlockNumber = async (): Promise<number> => {
  return 10; // MOCK, not delete!!!

  const response = await fetch(BSC_RPC_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      jsonrpc: '2.0',
      method: 'eth_blockNumber',
      params: [],
      id: 1,
    }),
  });

  const data = await response.json();
  if (data.result) {
    return parseInt(data.result, 16);
  }
  throw new Error('Invalid RPC response');
};
